import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, ClipboardCheck, GitBranch, BookOpen, PenLine, Building2, LogOut, Sparkles, Users2 } from "lucide-react";

const nav = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/app/diagnostic/results", label: "My EQ Profile", icon: ClipboardCheck, tid: "nav-diagnostic" },
  { to: "/app/scenarios", label: "Simulations", icon: GitBranch, tid: "nav-scenarios" },
  { to: "/app/lessons", label: "Microlearning", icon: BookOpen, tid: "nav-lessons" },
  { to: "/app/journal", label: "Journal", icon: PenLine, tid: "nav-journal" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const nav_ = useNavigate();
  const leader = ["school_leader", "district_leader", "admin"].includes(user?.role);
  const admin = ["district_leader", "admin"].includes(user?.role);

  return (
    <div className="min-h-screen flex bg-[#FDFBF7] grain-bg">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white/70 backdrop-blur-sm">
        <div className="px-6 py-8 border-b border-slate-100">
          <Link to="/app" className="flex items-center gap-2" data-testid="brand-link">
            <div className="h-9 w-9 rounded-lg bg-[#0B1E36] grid place-items-center">
              <Sparkles className="h-4 w-4 text-[#C9A961]" />
            </div>
            <div>
              <div className="font-serif text-lg leading-tight text-[#0B1E36]">Future Counts OS™</div>
              <div className="text-[10px] tracking-widest text-slate-500 uppercase">by the Collective</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {nav.map((n) => {
            const Icon = n.icon;
            const active = location.pathname === n.to || (n.to !== "/app" && location.pathname.startsWith(n.to));
            return (
              <Link
                key={n.to} to={n.to} data-testid={n.tid}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active ? "bg-[#0B1E36] text-white" : "text-slate-700 hover:bg-slate-100"
                }`}>
                <Icon className="h-4 w-4" />
                {n.label}
              </Link>
            );
          })}
          {leader && (
            <Link
              to="/app/system" data-testid="nav-system"
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === "/app/system" ? "bg-[#0B1E36] text-white" : "text-slate-700 hover:bg-slate-100"
              }`}>
              <Building2 className="h-4 w-4" />
              System Dashboard
            </Link>
          )}
          {admin && (
            <Link
              to="/app/partners-admin" data-testid="nav-partners-admin"
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                location.pathname === "/app/partners-admin" ? "bg-[#0B1E36] text-white" : "text-slate-700 hover:bg-slate-100"
              }`}>
              <Users2 className="h-4 w-4" />
              Partner Applications
            </Link>
          )}
        </nav>
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-[#EC4899] text-white grid place-items-center font-serif font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-slate-500 capitalize truncate">{user?.role?.replace("_", " ")}</div>
            </div>
            <button
              onClick={() => { logout(); nav_("/"); }} data-testid="logout-btn"
              className="text-slate-400 hover:text-[#EC4899] p-1.5 rounded-md hover:bg-slate-100">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        {children}
      </main>
    </div>
  );
}
